import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import {
  albumModes,
  downloadPhoto,
  fetchPhotos,
  hasMore,
  modeShown,
  type Counts,
  type Mode,
  type Photo,
} from "../album/photos";
import { isBeside } from "../album/cadre";
import { albumColumns } from "../album/colonnes";
import { Portrait } from "../album/Portrait";
import { useEvent } from "../context/EventProvider";
import { logAction, useAccessLog } from "../log/actionLog";
import { useParticipation } from "../funnel/useParticipation";
import { fallbackOf } from "../settings/catalog";
import { useFlagOr, useSettings, useT, useText } from "../settings/context";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Prose } from "../ui/Prose";
import { scrollingPane } from "../ui/scroll";
import { InPlace } from "../panel/InPlace";

/**
 * ═══ L'ALBUM ═══
 *
 * Les photos de l'événement : celles où le participant a été reconnu, et celles
 * d'ambiance, que la route rend ensemble.
 *
 * ⚠️ PAR PAGES, PARCE QUE LA ROUTE EST PAGINÉE ET QU'UN ALBUM SE COMPTE EN CENTAINES.
 * Tout demander d'un coup, c'est autant d'adresses signées à fabriquer côté serveur et
 * autant d'images à charger pour un écran qui en montre huit. On demande la suite quand
 * on la réclame.
 *
 * ⚠️ CE QUI N'EST PAS REPRIS DE LA MAQUETTE, ET POURQUOI. Elle pose deux boutons de plus :
 * « Tout télécharger » et « Lancer le diaporama (borne) ». Le premier demande une archive
 * que l'API ne fabrique pas — enchaîner N téléchargements se fait bloquer par le
 * navigateur dès le second — et le mode borne est un écran à part entière, pas une
 * variante de celui-ci. Les promettre par un interrupteur qui ne fait rien serait pire
 * que de les attendre.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */

/** Combien de photos par page. Assez pour remplir deux rangées de quatre. */
const PER_PAGE = 24;

/* Chaque onglet porte sa clé de dictionnaire ; le français reste le repli. */
const TAB_LABELS: Record<Mode, readonly [key: string, french: string]> = {
  all: ["ALBUM_TAB_ALL", "Toutes"],
  mine: ["ALBUM_TAB_MINE", "Mes photos"],
  ambiance: ["ALBUM_TAB_AMBIANCE", "Ambiance"],
};

export function Album() {
  const t = useT();
  const event = useEvent();
  useAccessLog("albumAccess");
  const { me, unavailable } = useParticipation();

  const title = useText(
    "FO_MEETING_V2_ALB_TITLE",
    fallbackOf("FO_MEETING_V2_ALB_TITLE"),
  );
  const subtitle = useText(
    "FO_MEETING_V2_ALB_SUBTITLE",
    fallbackOf("FO_MEETING_V2_ALB_SUBTITLE"),
  );
  const html = useText("FO_MEETING_V2_ALB_HTML");
  const iaShown = useFlagOr("FO_MEETING_V2_ALB_IA");
  /* Laisser le participant poser sa propre photo de référence : éteint par défaut,
     parce que cela envoie un portrait à la reconnaissance et que c'est à l'organisateur
     de le décider. */
  const faceShown = useFlagOr("FO_MEETING_V2_ALB_FACE");
  const modes = albumModes(useFlagOr("FO_MEETING_V2_ALB_AMBIANCE"));
  /* Le nombre de colonnes choisi par l'organisateur, posé en variables CSS sur la
     grille : la feuille de style garde la main sur le point de bascule mobile/bureau. */
  const { value } = useSettings();
  const cols = albumColumns(value("FO_MEETING_V2_ALB_COLUMNS"));
  const aiText = useText(
    "FO_MEETING_V2_ALB_IA_TEXT",
    fallbackOf("FO_MEETING_V2_ALB_IA_TEXT"),
  );

  /**
   * ⚠️ LE MODE EST DANS LA CLÉ DE LA REQUÊTE, sinon changer de filtre garderait les
   * pages déjà chargées de l'autre : on cliquerait « Mes photos » pour voir arriver
   * les ambiances qu'on venait de charger, et la pagination repartirait au milieu.
   */
  const [chosen, setMode] = useState<Mode>("all");
  const mode = modeShown(chosen, modes);

  const q = useInfiniteQuery({
    queryKey: ["photos", event?.domainId, event?.eventId, me?.id, mode],
    queryFn: ({ pageParam, signal }) =>
      fetchPhotos(event!.domainId, event!.eventId, pageParam, PER_PAGE, mode, signal),
    initialPageParam: 0,
    /**
     * ⚠️ ON COMPTE CE QUE LE SERVEUR A ENVOYÉ, PAS CE QU'ON A GARDÉ. Le test portait
     * sur `photos.length`, c'est-à-dire sur la liste APRÈS filtrage : une seule ligne
     * inutilisable dans les vingt-quatre suffisait à faire croire à l'album qu'il
     * était au bout. Vu sur un événement réel — neuf photos affichées, cent
     * trente et une annoncées, et plus rien qui se charge en défilant.
     */
    getNextPageParam: (last, all) =>
      hasMore(last, PER_PAGE) ? all.length : undefined,
    enabled: !!event && typeof me?.id === "number",
    retry: false,
  });

  const pages = q.data?.pages ?? [];
  const photos: Photo[] = pages.flatMap((p) => p.photos);

  /**
   * COMBIEN DE PHOTOS EN TOUT, pas seulement combien sont arrivées.
   *
   * ⚠️ SANS ÇA, LA VISIONNEUSE COMPTAIT CE QU'ELLE AVAIT SOUS LA MAIN et annonçait
   * « 1 / 24 » au milieu d'un album de 1733, un nombre qui grandissait à mesure qu'on
   * défilait. Le serveur le dit maintenant dans un en-tête ; tant qu'il ne le dit pas, on
   * se rabat sur ce qui est chargé, ce qui redonne l'ancien comportement plutôt qu'un
   * chiffre inventé.
   */
  const total = pages.find((p) => p.total !== null)?.total ?? photos.length;

  /**
   * ═══ LES COMPTES DES TROIS PILES, ET POURQUOI ILS SURVIVENT AU CHANGEMENT ═══
   *
   * ⚠️ ILS DÉCRIVENT L'ALBUM, PAS LA PAGE AFFICHÉE — le serveur les calcule sur tout
   * l'album, quel que soit l'onglet demandé. Lus seulement dans la requête en cours,
   * ils DISPARAISSAIENT à chaque clic : changer d'onglet ouvre une nouvelle requête,
   * qui n'a pas encore de données, donc les trois nombres s'effaçaient le temps de
   * l'aller-retour. Invisible en local (vingt millisecondes), très visible sur le
   * serveur — reproduit à 1,5 s de latence.
   *
   * On garde donc le dernier connu : c'est le même nombre, il ne se périme pas d'un
   * onglet à l'autre.
   */
  const [counts, setCounts] = useState<Counts | null>(null);
  const fresh = pages.find((p) => p.counts !== null)?.counts ?? null;
  useEffect(() => {
    if (fresh) setCounts(fresh);
    /* Dépendance sur les VALEURS : l'objet est recréé à chaque rendu, et le lister
       lui-même relancerait l'effet en boucle. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fresh?.all, fresh?.mine, fresh?.ambiance]);

  /** La photo ouverte en grand, par sa place dans la liste. */
  const [open, setOpen] = useState<number | null>(null);
  const current = open === null ? null : (photos[open] ?? null);

  const download = useMutation({
    mutationFn: downloadPhoto,
    // Le nom d'origine du fichier, et l'onglet d'où il part : « DSC_7286.jpg · Mes photos ».
    onSuccess: (_done, photo) =>
      logAction(event, "photoDownload", {
        label: `${photo.fileName || `photo_${photo.id}.jpg`} · ${t(...TAB_LABELS[mode])}`,
      }),
  });

  /**
   * ═══ THE NEXT PAGE ARRIVES BY SCROLLING ═══
   *
   * A marker sits under the grid and the browser tells us when it comes into view. The
   * margin makes it come into view EARLY, a screenful before it is reached, so the
   * pictures are there by the time the eye gets to them rather than after.
   *
   * ⚠️ THE OBSERVER IS ONLY ATTACHED WHEN A LOAD IS POSSIBLE, and that is what chains the
   * pages together. When a page lands the effect runs again, and if the marker is STILL in
   * view — which is the case for someone scrolling fast, or on a tall screen — observing
   * it fires straight away and the next page starts. No polling, no interval.
   */
  const sentinel = useRef<HTMLDivElement | null>(null);
  const [stopped, setStopped] = useState(false);

  /* ⚠️ ET IL SE RELÂCHE EN CHANGEANT D'ONGLET. `mode` fait partie de la clé de la
     requête : un autre onglet, c'est une autre liste, qui a droit à sa chance. Sans
     ça, une page ratée sur un onglet laissait le marqueur détaché pour tous les
     suivants — on faisait défiler, plus rien ne venait, et seul « Charger la suite »
     s'en sortait. */
  useEffect(() => {
    setStopped(false);
  }, [mode]);

  useEffect(() => {
    const target = sentinel.current;
    if (!target || stopped || !q.hasNextPage || q.isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        /* ⚠️ AND IT STOPS ITSELF ON A FAILURE. Without this, a page that does not load
           leaves the marker on screen, the effect re-attaches, the observer fires again,
           and the same request is replayed as fast as the network answers. */
        void q.fetchNextPage().then((r) => {
          if (r.isError) setStopped(true);
        });
      },
      /* One screenful of lookahead, no more. Measured at 150%: three pages, 72 pictures,
         landed before the first scroll — paid for by everyone who opens the album and
         looks at the first row. One screen is enough to stay ahead of a thumb. */
      { root: scrollingPane(target), rootMargin: "100% 0px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [stopped, q.hasNextPage, q.isFetchingNextPage, q.fetchNextPage]);

  /**
   * ═══ THE BIG PICTURE TAKES ITS TIME ═══
   *
   * The viewer does not show the thumbnail again, it fetches the full file: several
   * hundred kilobytes, on event wifi. Between the click and the picture there was a black
   * rectangle and nothing else, which reads as a viewer that failed to open.
   *
   * ⚠️ THE STATE IS SET FROM THE REF, NOT FROM AN EFFECT ON `ouverte`. A picture already
   * in the browser cache is `complete` the moment the element exists, and its `load` event
   * has already been and gone — so waiting for `onLoad` would spin forever on exactly the
   * pictures that are instant. Doing the reset here too, rather than in a second effect,
   * avoids the two racing: refs run at commit, effects run after, and the effect would
   * have put a ready picture back to loading.
   */
  const [fullImage, setFullImage] = useState<"loading" | "ready" | "failed">(
    "loading",
  );
  const watchImage = useCallback((node: HTMLImageElement | null) => {
    if (!node) return;
    setFullImage(node.complete && node.naturalWidth > 0 ? "ready" : "loading");
  }, []);

  /**
   * ═══ ET LA VIGNETTE, ELLE AUSSI, MET UN INSTANT À VENIR ═══
   *
   * ⚠️ CHANGER LE `src` D'UNE IMAGE N'EFFACE PAS CELLE QUI EST À L'ÉCRAN. Le navigateur
   * continue d'afficher l'ancienne jusqu'à ce que la nouvelle soit décodée — mesuré, la
   * balise portait déjà l'adresse de la photo 2 avec `complete: false` et
   * `naturalWidth: 0`, pendant que la photo 1 restait peinte. La visionneuse annonçait
   * donc « 2 / 1733 » au-dessus de la photo 1, ce qui est pire que rien du tout.
   *
   * On retient donc QUELLE photo la balise a réellement peinte, et on la cache tant que
   * ce n'est pas celle qu'on prétend montrer. L'élément, lui, n'est pas remplacé : il
   * garde ses dimensions pendant l'attente, et c'est ce qui évite que la fenêtre se
   * replie sur elle-même entre deux photos.
   *
   * Ce délai vient de ce que la « vignette » fait encore 3216 px de large : il se réduira
   * à quelques millisecondes quand les `_XS` du bucket auront été refaits.
   */
  const [thumbFor, setThumbFor] = useState<number | null>(null);
  useEffect(() => {
    /* Refermée : la prochaine ouverture repart sur une balise neuve, donc sans passé. */
    if (open === null) setThumbFor(null);
  }, [open]);

  /**
   * Y a-t-il quelque chose à regarder ? C'est vrai dès que la vignette de LA photo
   * demandée est peinte, avant même que la pleine soit arrivée.
   *
   * ⚠️ ET C'EST LA SEULE CHOSE QUI DÉCIDE DU TOURNIQUET. Le faire tourner pendant que la
   * pleine se charge revenait à annoncer une attente au-dessus d'une photo déjà visible :
   * on lit « ça charge » alors que ce qu'on regarde est exactement ce qu'on a demandé,
   * juste un peu moins net. Il ne reste que là où l'écran est vraiment vide.
   */
  const photoVisible = current !== null && thumbFor === current.id;

  /**
   * ═══ GOING PAST THE LAST PICTURE LOADED ═══
   *
   * ⚠️ THE ARROW USED TO STOP AT WHAT WAS IN MEMORY, which was invisible while the counter
   * lied and said 24 of 24. Now that it says 24 of 1733, an arrow that refuses to move
   * reads as a broken viewer. So it asks for the next page and steps forward once it
   * lands — the effect is what carries the move across, because `fetchNextPage` resolving
   * does not by itself make the new picture reachable in this render.
   */
  const [awaitingPage, setAwaitingPage] = useState(false);

  const goNext = useCallback(() => {
    const at = open ?? 0;
    if (at + 1 < photos.length) {
      setOpen(at + 1);
      return;
    }
    if (!q.hasNextPage || q.isFetchingNextPage) return;
    setAwaitingPage(true);
    void q.fetchNextPage().then((r) => {
      if (r.isError) setAwaitingPage(false);
    });
  }, [open, photos.length, q.hasNextPage, q.isFetchingNextPage, q.fetchNextPage]);

  useEffect(() => {
    if (!awaitingPage) return;
    /* Closed in the meantime: drop the request to move rather than reopen on its own. */
    if (open === null) {
      setAwaitingPage(false);
      return;
    }
    if (open + 1 < photos.length) {
      setOpen(open + 1);
      setAwaitingPage(false);
    }
  }, [awaitingPage, open, photos.length]);

  /** Y a-t-il quelque chose après, chargé ou non ? */
  const moreAfter = (open ?? 0) + 1 < photos.length || !!q.hasNextPage;

  /**
   * ⚠️ LE CLAVIER FAIT PARTIE DE LA VISIONNEUSE, il n'en est pas l'ornement. On regarde
   * ses photos l'une après l'autre : sans les flèches, il faut refermer et rouvrir à
   * chaque fois, et sans Échap on cherche la croix.
   */
  useEffect(() => {
    if (open === null) return;
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft")
        setOpen((i) => (i === null ? null : Math.max(i - 1, 0)));
    };
    document.addEventListener("keydown", auClavier);
    return () => document.removeEventListener("keydown", auClavier);
  }, [open, goNext]);

  return (
    <section className="page">
      {/**
       * ═══ L'EN-TÊTE : LE TITRE, ET SA PHOTO SUR LA MÊME LIGNE ═══
       *
       * Titre et ligne-portrait partagent un en-tête (`.alb__hd`) pour une raison de
       * place : repliée, la ligne se réduit à « Gérer ma photo », et seule dans un rang
       * elle creusait un trou entre le sous-titre et la mention. Elle se cale donc à
       * droite du titre, au niveau du sous-titre. Dépliée — ou sans photo — elle reprend
       * toute la largeur, sous le titre : c'est le `flex-wrap` du conteneur qui décide,
       * pas un point de rupture (voir `funnel.css`).
       */}
      <div className="alb__hd">
        <div className="ttl" data-cfg="albTitle">
          <InPlace settingKey="FO_MEETING_V2_ALB_TITLE" text={title} as="h2" />
          <InPlace settingKey="FO_MEETING_V2_ALB_SUBTITLE" text={subtitle} as="p" />
        </div>

        {/* Sa photo de référence, et la recherche qu'elle déclenche. En tête, au-dessus
            des onglets : celui qui n'a encore aucune photo n'ouvrira jamais « Mes
            photos », et c'est précisément lui qui a besoin de ce bloc. */}
        {faceShown && !unavailable && <Portrait userId={me?.id} />}
      </div>

      {/* Le bloc libre de l'organisateur, sous le titre : son HTML, assaini comme
          partout (`ui/Prose`). Vide, rien ne s'affiche. */}
      {html.trim() !== "" && (
        <Prose html={html} className="se-prose" data-cfg="albTitle" />
      )}

      {/* La mention de la reconnaissance : elle explique POURQUOI l'album se remplit
          tout seul, et pourquoi il peut être vide juste après l'événement. */}
      {iaShown && aiText.trim() !== "" && (
        <p className="hint" data-cfg="albIA">
          {aiText}
        </p>
      )}

      {/**
       * ═══ MES PHOTOS, OU CELLES DE TOUT LE MONDE ═══
       *
       * ⚠️ IL NE S'AFFICHE QUE S'IL Y A À CHOISIR. Sur un événement sans photo
       * d'ambiance — ou dont la reconnaissance n'a encore rien trouvé — trois onglets
       * dont deux vides se lisent comme un album cassé.
       *
       * ⚠️ ET IL RECHARGE, il ne trie pas ce qui est là. Voir `album/photos.ts` : la
       * liste est paginée, un tri local mentirait sur ce qu'il cache.
       */}
      {/**
       * ⚠️ ET IL SURVIT À SON PROPRE CLIC. La condition ne regardait que la page
       * affichée : revenir sur « Toutes » ouvre une nouvelle requête, sans données,
       * donc `photos` vide et `mode === "all"` — la commande qu'on venait de toucher
       * disparaissait le temps de l'aller-retour, et la page sautait avec elle. Même
       * défaut que les nombres qui s'effaçaient, à la ligne au-dessus. Les comptes
       * décrivent l'album entier et sont gardés : dès qu'on les connaît, il y a de
       * quoi choisir, et la barre reste.
       *
       * ⚠️ Y COMPRIS APRÈS UNE ERREUR, et c'est le même piège : `!q.isError` retirait
       * la barre au moment où l'onglet ouvert venait d'échouer, donc le seul moyen
       * d'en sortir. Le message d'erreur se lit plus bas ; la sortie doit rester.
       */}
      {!unavailable && (counts !== null || photos.length > 0 || mode !== "all") ? (
        <div
          className="rg__seg alb__f"
          role="group"
          aria-label={t("DISPLAYED_PHOTOS", "Photos affichées")}
          data-cfg="albTabs"
        >
          {modes.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={mode === value}
              onClick={() => {
                setMode(value);
                /* La visionneuse pointe une PLACE dans la liste : la garder ouverte
                   sur un autre filtre montrerait une autre photo sous le même rang. */
                setOpen(null);
              }}
            >
              {/* Le nombre seulement si le serveur le dit : voir `PagePhotos.counts`. */}
              {counts
                ? `${t(...TAB_LABELS[value])} (${counts[value]})`
                : t(...TAB_LABELS[value])}
            </button>
          ))}
        </div>
      ) : null}

      {q.isPending && !unavailable && (
        <p className="empty">
          <span className="spin" aria-hidden="true" />
          <span className="a11y" role="status">
            {t("LOADING_YOUR_PHOTOS", 'Chargement de vos photos')}
          </span>
        </p>
      )}

      {/* `unavailable` : l'identité n'a pas pu être lue (échec non-401), donc la requête
          reste désactivée et `isPending` pour toujours. On le dit plutôt que de tourner. */}
      {(q.isError || unavailable) && (
        <p className="loginerr" role="alert">
          {t("PHOTOS_NOT_LOADED", "Vos photos n'ont pas pu être chargées. Réessayez dans un instant.")}
        </p>
      )}

      {photos.length > 0 && (
        <div
          className="album"
          data-cfg="albGrid"
          style={{ "--alb-cols": cols.desktop, "--alb-cols-m": cols.mobile } as CSSProperties}
        >
          {photos.map((photo, i) => (
            <button
              type="button"
              className="album__i"
              key={photo.id}
              aria-label={t("ENLARGE_PHOTO_N_OF_M", "Agrandir la photo $1 sur $2", [
                i + 1,
                total,
              ])}
              onClick={() => setOpen(i)}
            >
              {/* ⚠️ `loading="lazy"` : un album de trois cents photos chargerait trois
                  cents images pour en montrer huit. Le navigateur sait ne demander que
                  ce qui approche de l'écran, il suffit de le lui dire. */}
              <img src={photo.vignette} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {/* The marker the browser watches. It shows nothing at all except while a page is
          on its way, or once one has failed and it takes a gesture to start again. */}
      {q.hasNextPage && (
        <div className="album__more" ref={sentinel}>
          {q.isFetchingNextPage && <span className="spin" aria-hidden="true" />}
          {stopped && (
            <Button type="button" variant="ghost" onClick={() => setStopped(false)}>
              {t("LOAD_MORE", "Charger la suite")}
            </Button>
          )}
          {/* ⚠️ WHAT HAPPENS ON ITS OWN STILL HAS TO BE ANNOUNCED. A grid that grows in
              silence does not exist for anyone who cannot see it scroll. */}
          <span className="a11y" role="status">
            {q.isFetchingNextPage
              ? t("LOADING_NEXT_PHOTOS", "Chargement des photos suivantes")
              : ""}
          </span>
        </div>
      )}

      {/* ⚠️ UN ALBUM VIDE EST L'ÉTAT NORMAL AVANT L'ÉVÉNEMENT, et pendant les heures qui
          suivent : la reconnaissance travaille par lots. Sans un mot, la page se lit
          comme une panne, et on n'y revient pas. */}
      {!q.isPending && !q.isError && photos.length === 0 && (
        /* ⚠️ « VIDE ICI » N'EST PAS « VIDE ». Sous un filtre, le message d'attente
           annoncerait un album qui se remplira à quelqu'un qui a simplement choisi
           une pile qui n'a rien — et l'autre pile, elle, est pleine. */
        <p className="empty">
          {mode === "mine"
            ? t(
                "ALBUM_EMPTY_MINE",
                "Aucune photo de vous pour l'instant. La reconnaissance travaille par lots : revenez un peu plus tard, ou regardez les photos d'ambiance.",
              )
            : mode === "ambiance"
              ? t(
                  "ALBUM_EMPTY_AMBIANCE",
                  "Aucune photo d'ambiance sur cet événement.",
                )
              : t(
                  "ALBUM_EMPTY",
                  "Aucune photo pour l'instant. Elles arrivent pendant l'événement, et vous y apparaissez dès que vous avez été reconnu.",
                )}
        </p>
      )}

      {/* ═══ LA VISIONNEUSE ═══
          Le fond ferme, comme partout ailleurs dans ce site : une fenêtre dont on ne
          sort que par une petite croix est une fenêtre qu'on apprend à redouter. */}
      {current && (
        <div
          className="lb"
          role="dialog"
          aria-modal="true"
          aria-label={t("PHOTO_ENLARGED", "Photo en grand")}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div className="lb__bg" onClick={() => setOpen(null)} />
          <div className="lb__c">
            {/* ⚠️ LE CADRE FAIT TOUTE LA FENÊTRE, ET LA ZONE SOMBRE À CÔTÉ DE LA PHOTO EN
                FAIT PARTIE : un clic là n'atteint pas `.lb__bg`, alors qu'à l'œil c'est le
                fond, et le fond ferme. On refait donc le calcul du navigateur — où la photo
                `contain` est posée dans le cadre — et on ferme si le clic est tombé à côté.
                Voir `album/cadre.ts`. Tant qu'aucune image n'est décodée, on ne sait pas et
                on ne ferme pas. */}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <div
              className="lb__img"
              data-etat={fullImage}
              data-vignette={photoVisible ? "ready" : "attente"}
              onClick={(e) => {
                const frame = e.currentTarget.getBoundingClientRect();
                const painted = Array.from(e.currentTarget.querySelectorAll("img")).find(
                  (img) => img.naturalWidth > 0,
                );
                if (!painted) return;
                const natural = { width: painted.naturalWidth, height: painted.naturalHeight };
                if (isBeside(frame, natural, { x: e.clientX, y: e.clientY })) setOpen(null);
              }}
            >
              {/* ⚠️ LA VIGNETTE D'ABORD, ET ELLE EST DÉJÀ LÀ. On n'ouvre la visionneuse
                  qu'en cliquant dans la grille, donc cette image est forcément dans le
                  cache du navigateur. Elle donne quelque chose à regarder pendant
                  l'attente — la pleine a exactement les mêmes proportions, étant réduite
                  et non recadrée, donc elle prendra sa place au pixel près. */}
              <img
                src={current.vignette}
                alt=""
                aria-hidden="true"
                onLoad={() => setThumbFor(current.id)}
              />
              <img
                className="lb__hd"
                key={current.id}
                ref={watchImage}
                src={current.full}
                alt=""
                onLoad={() => setFullImage("ready")}
                onError={() => setFullImage("failed")}
              />
              {!photoVisible && <span className="spin" aria-hidden="true" />}
              {fullImage === "failed" && (
                <p className="lb__ko">
                  {t("PHOTO_NOT_LOADED", "La photo en grande taille n'a pas pu être chargée.")}
                </p>
              )}
              <span className="a11y" role="status">
                {photoVisible ? "" : t("LOADING_PHOTO", "Chargement de la photo")}
              </span>
            </div>
            <div className="lb__a">
              <button
                type="button"
                disabled={open === 0}
                aria-label={t("PREVIOUS_PHOTO", "Photo précédente")}
                onClick={() => setOpen((i) => Math.max((i ?? 0) - 1, 0))}
              >
                <Icon name="chevron" />
              </button>
              <span className="lb__n">
                {(open ?? 0) + 1} / {total}
              </span>
              <button
                type="button"
                className="lb__d"
                disabled={!moreAfter}
                aria-label={t("NEXT_PHOTO", "Photo suivante")}
                onClick={goNext}
              >
                {awaitingPage ? (
                  <span className="spin" aria-hidden="true" />
                ) : (
                  <Icon name="chevron" />
                )}
              </button>
              <Button
                type="button"
                loading={download.isPending}
                onClick={() => download.mutate(current)}
              >
                {t("DOWNLOAD", "Télécharger")}
              </Button>
            </div>
            {download.isError && (
              <p className="hint" role="alert">
                {/* Pas le message de l'erreur : `downloadPhoto` le lève en français,
                    en dur, et le site anglais l'affichait brut (bug #489). */}
                {t("PHOTO_NOT_DOWNLOADED", "La photo n'a pas pu être téléchargée.")}
              </p>
            )}
          </div>
          <button
            type="button"
            className="lb__x"
            aria-label={t("CLOSE", "Fermer")}
            onClick={() => setOpen(null)}
          >
            <Icon name="croix" />
          </button>
        </div>
      )}
    </section>
  );
}
